import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Image from '@tiptap/extension-image';
import styled from 'styled-components';
import { colors, spacing, typography, radius } from '../../design-system/tokens';
import {
  LinkIcon,
  ViewListIcon,
  PhotographIcon,
  TableIcon
} from '@heroicons/react/outline';

// Styled Components
const EditorContainer = styled.div<{ $minHeight?: number }>`
  background: transparent;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const DocumentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  display: flex;
  justify-content: center;
`;

const DocumentContainer = styled.div`
  width: 100%;
  background: transparent;
  position: relative;
`;

const EditorContentWrapper = styled.div`
  .ProseMirror {
    padding: 16px 24px;
    outline: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: ${colors.text.primary};

    
    @media (max-width: 768px) {
      padding: 12px 16px;
    }
    
    /* Headings */
    h1, h2, h3, h4, h5, h6 {
      margin: 1em 0 0.5em 0;
      font-weight: 600;
      color: ${colors.text.primary};
      
      &:first-child {
        margin-top: 0;
      }
    }
    
    h1 { 
      font-size: 1.8em; 
      line-height: 1.2;
      margin-bottom: 0.8em;
      font-weight: 700;
    }
    h2 { 
      font-size: 1.5em; 
      line-height: 1.3;
      margin-top: 1.8em;
    }
    h3 { 
      font-size: 1.3em; 
      line-height: 1.4;
      margin-top: 1.5em;
    }
    h4 { 
      font-size: 1.15em; 
      margin-top: 1.4em;
    }
    h5 { 
      font-size: 1.1em; 
      margin-top: 1.2em;
    }
    h6 { 
      font-size: 1em; 
      font-weight: 600;
      margin-top: 1.2em;
    }
    
    /* Paragraphs */
    p {
      margin: 0.8em 0;
      
      &:first-child {
        margin-top: 0;
      }
      
      &:last-child {
        margin-bottom: 0;
      }
      
      &.is-empty::before {
        content: attr(data-placeholder);
        color: ${colors.text.tertiary};
        pointer-events: none;
        float: left;
        height: 0;
      }
    }
    
    /* Links */
    a {
      color: ${colors.brand.primary};
      text-decoration: none;
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease;
      
      &:hover {
        border-bottom-color: ${colors.brand.primary};
      }
    }
    
    /* Lists */
    ul, ol {
      margin: 0.8em 0;
      padding-left: 1.5em;
      
      li {
        margin: 0.3em 0;
        
        p {
          margin: 0;
        }
        
        ul, ol {
          margin: 0.2em 0;
        }
      }
    }
    
    /* Code */
    code {
      background: ${colors.background.tertiary};
      padding: 0.25em 0.5em;
      border-radius: 4px;
      font-family: 'SF Mono', Consolas, monospace;
      font-size: 0.85em;
      color: ${colors.text.primary};
    }
    
    /* Code blocks */
    pre {
      background: ${colors.text.primary};
      color: ${colors.background.tertiary};
      padding: 1.2em;
      border-radius: 8px;
      overflow-x: auto;
      margin: 1.4em 0;
      
      code {
        background: none;
        padding: 0;
        color: inherit;
        font-size: 0.85em;
      }
    }
    
    /* Blockquotes */
    blockquote {
      border-left: 4px solid ${colors.brand.primary};
      margin: 1.4em 0;
      padding-left: 1.5em;
      font-style: italic;
      color: ${colors.text.secondary};
      font-size: 1em;
    }
    
    /* Horizontal rules */
    hr {
      border: none;
      height: 1px;
      background: ${colors.border.primary};
      margin: 2.4em 0;
    }
    
    /* Highlight */
    mark {
      background: linear-gradient(104deg, rgba(130, 255, 173, 0) 0.9%, rgba(130, 255, 173, 1.25) 2.4%, rgba(130, 255, 173, 0.5) 5.8%, rgba(130, 255, 173, 0.1) 93%, rgba(130, 255, 173, 0.7) 96%, rgba(130, 255, 173, 0) 98%), linear-gradient(183deg, rgba(130, 255, 173, 0) 0%, rgba(130, 255, 173, 0.3) 7.9%, rgba(130, 255, 173, 0) 15%);
      padding: 0.125em 0.25em;
      border-radius: 3px;
    }
    
    /* Strong text */
    strong {
      font-weight: 600;
    }
    
    /* Emphasis */
    em {
      font-style: italic;
    }
    
    /* Tables */
    table {
      border-collapse: collapse;
      margin: 1.2em 0;
      table-layout: fixed;
      width: 100%;
      overflow-x: auto;
    }
    
    th, td {
      border: 1px solid ${colors.border.primary};
      padding: 6px 10px;
      text-align: left;
      vertical-align: top;
      min-width: 80px;
      position: relative;
    }
    
    th {
      background-color: ${colors.background.secondary};
      font-weight: 600;
      color: ${colors.text.primary};
    }
    
    td {
      background-color: white;
    }
    
    /* Selected table cells */
    .selectedCell {
      background-color: ${colors.brand.primarySurface};
    }
    
    /* Column resize handle */
    .column-resize-handle {
      background-color: ${colors.brand.primary};
      bottom: -2px;
      position: absolute;
      right: -2px;
      top: 0;
      width: 4px;
      z-index: 20;
    }
    
    /* Images */
    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 1em 0;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transition: box-shadow 0.15s ease;
      
      &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
      }
      
      &.ProseMirror-selectednode {
        outline: 2px solid ${colors.brand.primary};
        outline-offset: 2px;
      }
    }
  }
`;

// Fixed Toolbar Components
const FixedToolbar = styled.div`
  background: white;
  border-bottom: 1px solid ${colors.border.primary};
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  
  &:not(:last-child)::after {
    content: '';
    width: 1px;
    height: 20px;
    background: ${colors.border.primary};
    margin: 0 8px;
  }
`;

const ToolbarButton = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: ${props => props.$isActive ? colors.brand.primarySurface : 'transparent'};
  color: ${props => props.$isActive ? colors.brand.primary : '#222429'};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${props => props.$isActive ? colors.brand.primary : colors.background.tertiary};
    color: ${props => props.$isActive ? 'white' : '#222429'};
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    
    &:hover {
      background: transparent;
      color: #222429;
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ToolbarDropdown = styled.select<{ $isActive?: boolean }>`
  border: none;
  background: ${props => props.$isActive ? colors.brand.primarySurface : 'transparent'};
  color: ${props => props.$isActive ? colors.brand.primary : '#222429'};
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${props => props.$isActive ? colors.brand.primary : colors.background.tertiary};
    color: ${props => props.$isActive ? 'white' : '#222429'};
  }
  
  &:focus {
    outline: none;
    background: ${colors.brand.primarySurface};
  }
`;

// Types
interface TiptapEditorProps {
  content?: string | any;
  onChange?: (content: any) => void;
  onEditorReady?: (editor: any) => void;
  placeholder?: string;
  minHeight?: number;
  editable?: boolean;
  toolbarPosition?: 'top' | 'bottom' | 'none';
}

// Fixed Toolbar Component
const EditorToolbar: React.FC<{ editor: any }> = ({ editor }) => {
  if (!editor) return null;

  const handleHeadingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const level = parseInt(e.target.value);
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  const getCurrentHeadingLevel = () => {
    if (editor.isActive('heading', { level: 1 })) return 1;
    if (editor.isActive('heading', { level: 2 })) return 2;
    if (editor.isActive('heading', { level: 3 })) return 3;
    return 0;
  };

  const handleImageUpload = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';
    
    fileInput.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          window.alert('Please select an image file (JPG, PNG, GIF, etc.)');
          return;
        }

        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          window.alert('Image file size must be less than 10MB');
          return;
        }

        try {
          const reader = new FileReader();
          reader.onload = () => {
            editor.chain().focus().setImage({ 
              src: reader.result as string,
              alt: file.name,
              title: file.name 
            }).run();
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error uploading image:', error);
          window.alert('Failed to upload image');
        }
      }
    };
    
    fileInput.click();
  };

  const handleLinkToggle = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = window.prompt('Enter the URL:');
      if (url) {
        const finalUrl = url.startsWith('http') ? url : `https://${url}`;
        editor.chain().focus().setLink({ href: finalUrl }).run();
      }
    }
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <FixedToolbar>
      {/* Text Style Dropdown */}
      <ToolbarSection>
        <ToolbarDropdown 
          value={getCurrentHeadingLevel()} 
          onChange={handleHeadingChange}
          $isActive={getCurrentHeadingLevel() > 0}
        >
          <option value={0}>Normal</option>
          <option value={1}>Heading 1</option>
          <option value={2}>Heading 2</option>
          <option value={3}>Heading 3</option>
        </ToolbarDropdown>
      </ToolbarSection>

      {/* Text Formatting */}
      <ToolbarSection>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          $isActive={editor.isActive('bold')}
          title="Bold"
        >
          <strong style={{ fontSize: '14px', fontWeight: 'bold' }}>B</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          $isActive={editor.isActive('italic')}
          title="Italic"
        >
          <em style={{ fontSize: '14px', fontStyle: 'italic' }}>I</em>
        </ToolbarButton>
      </ToolbarSection>

      {/* Lists */}
      <ToolbarSection>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          $isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <ViewListIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          $isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <span style={{ fontSize: '12px', fontWeight: 'bold' }}>1.</span>
        </ToolbarButton>
      </ToolbarSection>

      {/* Links */}
      <ToolbarSection>
        <ToolbarButton
          onClick={handleLinkToggle}
          $isActive={editor.isActive('link')}
          title={editor.isActive('link') ? 'Remove Link' : 'Add Link'}
        >
          <LinkIcon />
        </ToolbarButton>
      </ToolbarSection>

      {/* Insert */}
      <ToolbarSection>
        <ToolbarButton
          onClick={handleImageUpload}
          title="Insert Image"
        >
          <PhotographIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={insertTable}
          title="Insert Table"
        >
          <TableIcon />
        </ToolbarButton>
      </ToolbarSection>
    </FixedToolbar>
  );
};

export const TiptapEditor: React.FC<TiptapEditorProps> = ({
  content = '',
  onChange,
  onEditorReady,
  placeholder = 'Start writing your document...',
  minHeight = 400,
  editable = true,
  toolbarPosition = 'top'
}) => {
  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Helper function to handle image file upload
  const handleImageFile = async (file: File, editor: any) => {
    if (!file.type.startsWith('image/')) {
      window.alert('Please select an image file (JPG, PNG, GIF, etc.)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      window.alert('Image file size must be less than 10MB');
      return;
    }

    try {
      const base64 = await fileToBase64(file);
      editor.commands.setImage({ 
        src: base64,
        alt: file.name,
        title: file.name 
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      window.alert('Failed to upload image');
    }
  };
  
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-empty',
      }),
      Typography,
      Link.configure({
        openOnClick: false,
      }),
      Highlight.configure({
        multicolor: false,
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'editor-image',
        },
        allowBase64: true,
        inline: false,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getJSON());
      }
    },
    editorProps: {
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            // Create a temporary editor-like object with commands
            const editorCommands = {
              commands: {
                setImage: (attrs: any) => {
                  const { state, dispatch } = view;
                  const imageNode = state.schema.nodes.image.create(attrs);
                  const transaction = state.tr.replaceSelectionWith(imageNode);
                  dispatch(transaction);
                }
              }
            };
            handleImageFile(file, editorCommands);
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event, slice) => {
        if (event.clipboardData && event.clipboardData.files && event.clipboardData.files.length > 0) {
          const file = event.clipboardData.files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            // Create a temporary editor-like object with commands
            const editorCommands = {
              commands: {
                setImage: (attrs: any) => {
                  const { state, dispatch } = view;
                  const imageNode = state.schema.nodes.image.create(attrs);
                  const transaction = state.tr.replaceSelectionWith(imageNode);
                  dispatch(transaction);
                }
              }
            };
            handleImageFile(file, editorCommands);
            return true;
          }
        }
        return false;
      }
    }
  });

  // Call onEditorReady when editor is available
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Update content when prop changes
  useEffect(() => {
    if (!editor) return;
    
    // Handle content based on type
    try {
      if (typeof content === 'string') {
        // HTML string content
        if (content !== editor.getHTML()) {
          editor.commands.setContent(content, false);
        }
      } else if (content && typeof content === 'object') {
        // TipTap JSON content - compare JSON structures
        const currentContent = editor.getJSON();
        if (JSON.stringify(content) !== JSON.stringify(currentContent)) {
          editor.commands.setContent(content, false);
        }
      } else if (content === '') {
        // Empty content
        if (editor.getHTML() !== '<p></p>') {
          editor.commands.setContent('', false);
        }
      }
    } catch (error) {
      console.error('Error setting editor content:', error);
    }
  }, [content, editor]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  if (!editor) {
    return (
      <EditorContainer $minHeight={minHeight}>
        <DocumentWrapper>
          <DocumentContainer>
            <div style={{ 
              padding: '40px 20px', 
              textAlign: 'center',
              color: colors.text.tertiary
            }}>
              Loading editor...
            </div>
          </DocumentContainer>
        </DocumentWrapper>
      </EditorContainer>
    );
  }

  return (
    <EditorContainer $minHeight={minHeight}>
      {toolbarPosition === 'top' && <EditorToolbar editor={editor} />}
      <DocumentWrapper>
        <DocumentContainer>
          <EditorContentWrapper>
            <EditorContent editor={editor} />
          </EditorContentWrapper>
        </DocumentContainer>
      </DocumentWrapper>
      {toolbarPosition === 'bottom' && <EditorToolbar editor={editor} />}
    </EditorContainer>
  );
};

// Export EditorToolbar for external use
export { EditorToolbar }; 